<template>
  <SettingsModal
    :headline="$t('picklottery.quickpick.modal.headline')"
    icon="flash"
    :tracking="{ action: 'checkout', topicKey: 'quick_pick_settings' }"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-100 px-100 pb-300">
      <EditorPickLotteryQuickPickQuantity v-bind="{ max: maxQuantity }" v-model:value="quantity" />

      <EditorPickLotteryQuickPickBetType
        v-if="perPlayAddOn"
        v-bind="{ lotteryId }"
        v-model:value="betType"
        :add-on="perPlayAddOn"
      />

      <EditorPickLotteryQuickPickNumbers
        v-bind="{ config, disabledFields, errorMessage }"
        v-model:value="numbers"
      />

      <EditorPickLotteryQuickPickWager
        v-if="hasMultipleStakes"
        v-bind="{ config, priceList }"
        v-model:value="stake"
      />
    </div>

    <template #footer>
      <LcButton class="w-full" :disabled="!isValid" @click="emit('confirm')">
        {{ $t('picklottery.quickpick.modal.button', settings.quantity) }}
      </LcButton>
    </template>
  </SettingsModal>
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, PriceList } from '../@types/editor'

import { EDITOR_QUICK_PICK_MIN_QUANTITY } from '../constants/editor'
import { usePickLotteryStore } from '../stores/pickLottery'
import { validatePlay } from '../utils/editorPlay'

type Emits = {
  (event: 'close' | 'confirm'): void
}

type Props = {
  config: Config
  lotteryId: LotteryId
  maxQuantity: number
  priceList: PriceList
}

const emit = defineEmits<Emits>()
const { config, lotteryId, maxQuantity } = defineProps<Props>()

const { getTranslation, t } = useI18nUtils()
const store = usePickLotteryStore(lotteryId)

const settings = computed(() => store.quickPickSettings)

const betType = computed({
  get: () => settings.value.betType,
  set: (value) => store.setQuickPickSettings({ betType: value }),
})

const disabledFields = computed(() => store.getDisabledFieldNames(config, settings.value.betType))

const errorMessage = computed(() => {
  if (areNumbersValid.value) return

  return getTranslation({
    fallback: t('lottery.validation.field.numbers.notvalidforbettype'),
    key: `lottery.${lotteryId}.validation.field.numbers.notvalidforbettype`,
  })
})

const hasMultipleStakes = computed(() => config.stakes.length > 1)

const areNumbersValid = computed(() =>
  validatePlay(lotteryId, config, settings.value.betType, settings.value.numbers),
)

const isValid = computed(() => areNumbersValid.value && isQuantityValid.value)

const isQuantityValid = computed(() => {
  const { quantity } = settings.value
  return (
    Number.isInteger(quantity) &&
    quantity >= EDITOR_QUICK_PICK_MIN_QUANTITY &&
    quantity <= maxQuantity
  )
})

const numbers = computed({
  get: () => settings.value.numbers,
  set: (value) => store.setQuickPickSettings({ numbers: value }),
})

const perPlayAddOn = computed(() => config.addOns.find(({ perPlay }) => perPlay))

const quantity = computed({
  get: () => settings.value.quantity,
  set: (value) => store.setQuickPickSettings({ quantity: value }),
})

const stake = computed({
  get: () => settings.value.stake,
  set: (value) => store.setQuickPickSettings({ stake: value }),
})

watch(
  () => maxQuantity,
  (limit) => {
    const upperBound = Math.max(limit, EDITOR_QUICK_PICK_MIN_QUANTITY)
    const clamped = Math.min(Math.max(quantity.value, EDITOR_QUICK_PICK_MIN_QUANTITY), upperBound)
    if (clamped !== quantity.value) quantity.value = clamped
  },
  { immediate: true },
)
</script>
